import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { getUserSettings } from "@/lib/userSettings";
import { useTranslation } from "@/hooks/useTranslation";
import { HamburgerMenu } from "@/components/layout/HamburgerMenu";

interface WelcomeHeaderProps {
  headerFocusIndex?: number; // For spatial scanning: 0 = hamburger menu
}

export function WelcomeHeader({
  headerFocusIndex = -1,
}: WelcomeHeaderProps) {
  const navigate = useNavigate();
  const settings = getUserSettings();
  const { t, dir, isRTL } = useTranslation();

  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return t("goodMorning");
    if (hour >= 12 && hour < 17) return t("goodAfternoon");
    if (hour >= 17 && hour < 21) return t("goodEvening");
    return t("goodNight");
  };

  // Get gender-appropriate "what to do" text
  const getWhatToDo = () => {
    if (settings.gender === "female") return t("whatToDoFemale");
    if (settings.gender === "male") return t("whatToDoMale");
    return t("whatToDo");
  };

  const greeting = getGreeting();
  const name = settings.name || (isRTL ? "אורח" : "Guest");

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="flex items-center justify-between px-4 py-2 border-b border-border bg-card"
      dir={dir}
    >
      {/* Greeting - compact */}
      <div className="flex items-center gap-2">
        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="font-bold text-foreground"
          style={{ fontSize: 'calc(1.5rem * var(--font-scale, 1))' }}
        >
          {greeting},{" "}
          <span className="text-primary welcome-user-name">{name}</span>
        </motion.h1>
      </div>

      {/* Hamburger Menu only */}
      <div className="flex items-center">
        <HamburgerMenu 
          headerFocusIndex={headerFocusIndex}
          onRestMode={() => navigate("/rest")}
        />
      </div>
    </motion.header>
  );
}
